import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'; // Added useCallback
import axios from 'axios';
import apiService from '../services/api';

const AuthContext = createContext();

// Create a separate axios instance for authentication
const authApi = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000',
  headers: {
    'Content-Type': 'application/json',
  },
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  // Memoized function to load user permissions
  const loadUserPermissions = useCallback(async (token) => {
    try {
      const response = await authApi.get('/auth/user-permissions', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setPermissions(response.data.permissions || []);
    } catch (error) {
      console.error('Failed to load permissions:', error);
      setPermissions([]);
    }
  }, []); // No dependencies as it only uses setters and axios

  // Memoized function to check authentication status
  const checkAuthStatus = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await authApi.post('/auth/verify-token', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.valid) {
        // If the server issued a refreshed token, swap it in before using it
        const activeToken = response.data.new_token || token;
        if (response.data.new_token) {
          localStorage.setItem('authToken', response.data.new_token);
        }
        const user = response.data.user;
        setUser(user);
        setIsAuthenticated(true);
        await loadUserPermissions(activeToken);
        await loadUserSettings(user.user_id);
      } else {
        // Token is invalid, remove it
        localStorage.removeItem('authToken');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // If the server rejected the token (session expired/invalid), tell the login page why
      if (error.response?.status === 401) {
        localStorage.setItem('panorama_login_message', 'You have been signed out due to inactivity. Please sign in again.');
      }
      localStorage.removeItem('authToken');
    } finally {
      setIsLoading(false);
    }
  }, [loadUserPermissions]); // Dependency on loadUserPermissions

  // Check if user is authenticated on app load
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Periodically re-verify the token so it gets refreshed while the user is active.
  // Runs every 30 minutes; the server will issue a new token if expiry is within 10 minutes.
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => {
      checkAuthStatus();
    }, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, checkAuthStatus]);

  const login = async (username, password) => {
    try {
      const response = await authApi.post('/auth/login', {
        username,
        password
      });

      if (response.data.success) {
        const { token, user } = response.data;
        localStorage.setItem('authToken', token);
        setUser(user);
        setIsAuthenticated(true);
        await loadUserPermissions(token);
        await loadUserSettings(user.user_id);
        return user;
      } else {
        throw new Error(response.data.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Login failed. Please try again.');
    }
  };

  const googleLogin = async (credential) => {
    try {
      const response = await authApi.post('/auth/google-login', {
        token: credential
      });

      if (response.data.success) {
        const { token, user } = response.data;
        localStorage.setItem('authToken', token);
        setUser(user);
        setIsAuthenticated(true);
        await loadUserPermissions(token);
        await loadUserSettings(user.user_id);
        return user;
      } else {
        throw new Error(response.data.error || 'Google login failed');
      }
    } catch (error) {
      console.error('Google login error:', error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Google login failed. Please try again.');
    }
  };

  const logout = async () => {
    const token = localStorage.getItem('authToken');
    
    try {
      if (token) {
        await authApi.post('/auth/logout', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of API call success
      localStorage.removeItem('authToken');
      setUser(null);
      setIsAuthenticated(false);
      setPermissions([]);
    }
  };

  const hasPermission = (permissionName) => {
    return permissions.some(permission => permission.permission_name === permissionName);
  };

  const hasAnyPermission = (permissionNames) => {
    return permissionNames.some(permissionName => hasPermission(permissionName));
  };

  const hasRole = (roleName) => {
    return user?.user_type === roleName;
  };

  const isAdmin = () => {
    return hasRole('super_admin') || hasRole('admin');
  };

  const canManageUsers = () => {
    return hasAnyPermission(['user.create', 'user.update', 'user.delete']);
  };

  const canManageCarriers = () => {
    return hasAnyPermission(['carrier.create', 'carrier.update', 'carrier.delete']);
  };

  const loadUserSettings = async (userId) => {
    try {
      const settings = await apiService.getUserSettings(userId);
      if (settings.userLanguage) {
        localStorage.setItem('userLanguage', JSON.stringify(settings.userLanguage));
      }
      if (settings.contentLanguage) {
        localStorage.setItem('contentLanguage', JSON.stringify(settings.contentLanguage));
      }
      // Add other settings here
    } catch (error) {
      console.error('Failed to load user settings:', error);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    permissions,
    login,
    googleLogin,
    logout,
    hasPermission,
    hasAnyPermission,
    hasRole,
    isAdmin,
    canManageUsers,
    canManageCarriers,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
